import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe } from '@fortawesome/pro-light-svg-icons/faGlobe';
import { TypedMemo } from '../../common/types';
import { Panel, PanelBody, PanelHead } from '../../music24/components/primitives/Panel';
import { ExternalLink } from '../../common/views/externalLink/ExternalLink';
import { LockedControl } from '../../music24/components/LockedControl/LockedControl';
import { useMusic24Capabilities } from '../../music24/hooks/useMusic24Capabilities';
import { useUpgradePrompt } from '../../music24/hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../music24/config/music24PlanCapabilities';
import { helpArticleUrl } from '../../support/helpCenter/helpCenterUrl';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole profile editor; each part uses its own slice of it */
import styles from './SMLProfile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProfileCustomDomainCardProps {
  customDomain: string | null;
}

const CUSTOM_DOMAIN_FAQ_URL = helpArticleUrl('smartlinks', 'page-address-and-domains');

/** The `<a>` chunk both messages carry, hoisted so it is one component type. */
const faqLink = (chunks: React.ReactNode[]) => <ExternalLink href={CUSTOM_DOMAIN_FAQ_URL}>{chunks}</ExternalLink>;

/**
 * A domain the artist owns — a note, not a form.
 *
 * The account PATCH takes name, address, host and avatar and nothing else, and
 * the product has no DNS check and no certificate step, so there is nothing
 * here for an artist to fill in. Setting one up is a conversation with support.
 */
const ProfileCustomDomainCardComponent: React.FC<ProfileCustomDomainCardProps> = ({ customDomain }) => {
  const intl = useIntl();
  const { canUseCustomDomain } = useMusic24Capabilities();
  const openUpgradePrompt = useUpgradePrompt();
  const openCustomDomainPrompt = useCallback(
    () => openUpgradePrompt(SMLUpgradeFeature.customDomain),
    [openUpgradePrompt]
  );

  return (
    <Panel>
      <PanelHead
        icon={<FontAwesomeIcon icon={faGlobe} />}
        title={intl.formatMessage({ id: 'app.sml.profile.custom-domain.title', defaultMessage: 'Custom domain' })}
      />
      <PanelBody stack>
        {canUseCustomDomain ? (
          <span className={styles.fieldHelp}>
            {customDomain === null
              ? intl.formatMessage(
                  {
                    id: 'app.sml.profile.custom-domain.unset',
                    defaultMessage:
                      'Want your pages on a domain you own? <a>Contact support</a> and we will set it up for you.',
                  },
                  { a: faqLink }
                )
              : intl.formatMessage(
                  {
                    id: 'app.sml.profile.custom-domain.set',
                    defaultMessage: 'Your pages answer to {domain}. To change it, <a>contact support</a>.',
                  },
                  { domain: <span className={styles.fieldStrong}>{customDomain}</span>, a: faqLink }
                )}
          </span>
        ) : (
          <span className={styles.fieldHelp}>
            {intl.formatMessage({
              id: 'app.sml.profile.customDomain.locked',
              defaultMessage: 'Custom domains come with Pro, Max and Ultra. We set it up for you.',
            })}{' '}
            <LockedControl
              variant="link"
              feature={SMLUpgradeFeature.customDomain}
              onClick={openCustomDomainPrompt}
              label={intl.formatMessage({ id: 'app.sml.profile.customDomain.upgrade', defaultMessage: 'Upgrade' })}
            />
          </span>
        )}
      </PanelBody>
    </Panel>
  );
};

export const ProfileCustomDomainCard = TypedMemo(ProfileCustomDomainCardComponent);
